import { Badge } from "@/components/ui/badge";
import { PPP_TYPE_DOT, PPP_TYPE_LABEL } from "@/components/ppp/ppp-constants";
import { PppLock } from "@/components/ppp/PppLock";
import { cn } from "@/lib/utils";
import { PriorityPppRef } from "@/types";
import { sortedPpps } from "./group-by-ppp";

interface PppChipsProps {
    ppps: PriorityPppRef[];
    isCandidate?: boolean;
    max?: number;
    /** Hide this PPP (the group header already names it). */
    omitPid?: string;
    className?: string;
}

export const CANDIDATE_BADGE_CLASS =
    "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300";

export function PppChips({ ppps, isCandidate, max = 2, omitPid, className }: PppChipsProps) {
    const shown = sortedPpps(ppps ?? []).filter((p) => p.pid !== omitPid);
    const visible = shown.slice(0, max);
    const extra = shown.length - visible.length;
    if (visible.length === 0 && !isCandidate) return null;

    return (
        <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
            {isCandidate && (
                <Badge variant="outline" className={cn("h-6 rounded-full px-2 text-[10px] font-medium shadow-none", CANDIDATE_BADGE_CLASS)}>
                    Candidate
                </Badge>
            )}
            {visible.map((p) => (
                <Badge
                    key={p.pid}
                    variant="outline"
                    title={`${PPP_TYPE_LABEL[p.type]} · ${p.name}`}
                    className="h-6 max-w-[10rem] gap-1.5 rounded-full border-border/60 bg-muted/40 px-2.5 text-[10px] font-medium text-muted-foreground shadow-none"
                >
                    <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", PPP_TYPE_DOT[p.type])} />
                    <span className="truncate">{p.name}</span>
                    {p.isConfidential && <PppLock />}
                </Badge>
            ))}
            {extra > 0 && <span className="text-[10px] text-muted-foreground">+{extra}</span>}
        </div>
    );
}
